import { useEffect, useRef } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatUIKit, CometChatUIKitLoginListener } from "@cometchat/chat-uikit-react";

/**
 * Notices when this tab's chat connection has quietly died, and gets it
 * back without the person having to work out that a reload would fix it.
 *
 * The failure this exists for: a CometChat auth token can stop working
 * while the page still looks completely fine. The socket drops, the UI
 * keeps rendering the last state it had, and messages sent to you simply
 * never arrive — no error, no banner, and to everyone else you fall off
 * presence and show as offline. That combination (silently missing
 * messages + looking offline while you're sat there) is close to
 * impossible for a user to diagnose; it just reads as "chat is broken".
 *
 * The original trigger was minting tokens with `force: true`, which
 * revoked every other session a person had, so signing in on one device
 * killed the tab open on another (see _lib/cometchat.js). That's fixed at
 * the source now, but a token can still lapse for ordinary reasons — a
 * genuine expiry, an admin revoking it, a long sleep/resume — so the
 * client shouldn't depend on the server never doing it.
 *
 * Recovery is the same call the gate's fast path already uses: hand the
 * signed local-session token to dbm-verify-session, get a fresh CometChat
 * auth token, log back in with it. No OTP, no user interaction.
 */

/** Matches DBMGate.tsx's LOCAL_SESSION_KEY. */
const LOCAL_SESSION_KEY = "dbm-local-session-token";

const VERIFY_SESSION_URL =
  process.env.REACT_APP_VERIFY_SESSION_URL || "/.netlify/functions/dbm-verify-session";

/**
 * How long a disconnect is allowed to last before we stop assuming the
 * SDK's own reconnect will handle it. Ordinary network blips recover in
 * a couple of seconds; a token that's no longer valid never will, and
 * retries forever without ever reporting anything.
 */
const RECONNECT_GRACE_MS = 12_000;

/** Floor between recovery attempts, so a genuinely offline machine (or a
 * function returning 401 because the 30-day session really has expired)
 * can't turn into a hot loop of fetches. */
const MIN_RETRY_GAP_MS = 30_000;

export function useSessionRecovery(): void {
  const timerRef = useRef<number | null>(null);
  const recoveringRef = useRef(false);
  const lastAttemptRef = useRef(0);

  useEffect(() => {
    const clearTimer = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const recover = async () => {
      if (recoveringRef.current) return;
      if (Date.now() - lastAttemptRef.current < MIN_RETRY_GAP_MS) return;
      // Already back on its own between the timer firing and this running.
      if (CometChat.getConnectionStatus() === CometChat.CONNECTION_STATUS.CONNECTED) return;

      const token = localStorage.getItem(LOCAL_SESSION_KEY);
      // No stored session means the gate never completed OTP on this
      // browser; there's nothing to silently restore from.
      if (!token) return;

      recoveringRef.current = true;
      lastAttemptRef.current = Date.now();
      try {
        const res = await fetch(VERIFY_SESSION_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.authToken) return;

        // logout() first: the SDK still believes it holds a valid session,
        // and logging in over the top of that is what leaves the two
        // disagreeing. Failure here is expected and fine — the session
        // being logged out of is the broken one.
        await CometChat.logout().catch(() => {});
        await CometChatUIKit.loginWithAuthToken(data.authToken);
      } catch {
        // Deliberately silent: this is a background self-heal. If it
        // can't fix things the person still has the same reload they'd
        // have had anyway, and the next disconnect event tries again.
      } finally {
        recoveringRef.current = false;
      }
    };

    const listenerId = `dbm-session-recovery-${Date.now()}`;
    CometChat.addConnectionListener(
      listenerId,
      new CometChat.ConnectionListener({
        onConnected: () => clearTimer(),
        inConnecting: () => {
          /* SDK is retrying on its own — the grace timer is already
             running and will fire if that never succeeds. */
        },
        onDisconnected: () => {
          clearTimer();
          timerRef.current = window.setTimeout(recover, RECONNECT_GRACE_MS);
        },
      })
    );

    /* Coming back to a backgrounded tab is the other way people meet this
       bug: the machine slept, the socket died, and nothing re-checks on
       wake. Only acts when the SDK itself agrees it's disconnected, so a
       healthy tab switch costs nothing. */
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!CometChatUIKitLoginListener.getLoggedInUser()) return;
      if (CometChat.getConnectionStatus() === CometChat.CONNECTION_STATUS.CONNECTED) return;
      clearTimer();
      timerRef.current = window.setTimeout(recover, 1_000);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimer();
      document.removeEventListener("visibilitychange", onVisible);
      CometChat.removeConnectionListener(listenerId);
    };
  }, []);
}
